import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import {
  TruckIcon,
  UserIcon,
  UsersIcon,
  MapPinIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import {
  createEnhancedTrip,
  getTripFormData,
  type TransportSettings,
  type TripFormData
} from '@/api/tripsApi'
import toast from 'react-hot-toast'

// Days of the week options
const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday', short: 'Mon' },
  { value: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { value: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { value: 'thursday', label: 'Thursday', short: 'Thu' },
  { value: 'friday', label: 'Friday', short: 'Fri' },
  { value: 'saturday', label: 'Saturday', short: 'Sat' },
  { value: 'sunday', label: 'Sunday', short: 'Sun' }
]

// Form validation schema
const enhancedTripSchema = z.object({
  routeId: z.string().min(1, 'Route is required'),
  vehicleId: z.string().min(1, 'Vehicle is required'),
  primaryDriverId: z.string().min(1, 'Primary driver is required'),
  backupDriverId: z.string().optional(),
  tripName: z.string().min(1, 'Trip name is required').max(100, 'Trip name too long'),
  tripType: z.enum(['pickup', 'dropoff', 'field_trip', 'emergency', 'custom']),
  startTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Must be in HH:MM format'),
  endTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Must be in HH:MM format'),
  operatingDays: z.array(z.string()).min(1, 'Select at least one operating day'),
  capacity: z.number().min(1, 'Capacity must be at least 1').max(100, 'Capacity too large'),
  selectedStudents: z.array(z.string()).min(1, 'At least one student is required'),
  autoCreateSessions: z.boolean()
}).refine((data) => {
  const start = new Date(`2000-01-01T${data.startTime}`)
  const end = new Date(`2000-01-01T${data.endTime}`)
  return start < end
}, {
  message: "End time must be after start time",
  path: ["endTime"]
})

type EnhancedTripForm = z.infer<typeof enhancedTripSchema>

interface EnhancedTripFormProps {
  onSuccess?: (trip: any) => void
  onCancel?: () => void
  initialData?: Partial<EnhancedTripForm>
  institutionId?: string
}

// Define local types based on TripFormData
type FormRoute = TripFormData['routes'][0]
type FormVehicle = TripFormData['vehicles'][0]
type FormDriver = TripFormData['drivers'][0]
type FormStudent = TripFormData['students'][0]

const EnhancedTripForm: React.FC<EnhancedTripFormProps> = ({
  onSuccess,
  onCancel,
  initialData,
  institutionId
}) => {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [transportSettings, setTransportSettings] = useState<TransportSettings | null>(null)
  const [routes, setRoutes] = useState<FormRoute[]>([])
  const [vehicles, setVehicles] = useState<FormVehicle[]>([])
  const [drivers, setDrivers] = useState<FormDriver[]>([])
  const [students, setStudents] = useState<FormStudent[]>([])
  const [selectedRoute, setSelectedRoute] = useState<FormRoute | null>(null)

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<EnhancedTripForm>({
    resolver: zodResolver(enhancedTripSchema),
    defaultValues: {
      tripType: 'pickup',
      startTime: '07:30',
      endTime: '08:30',
      operatingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], // Default to weekdays
      capacity: 50,
      autoCreateSessions: true, // Auto-create sessions for operating days
      selectedStudents: [],
      ...initialData
    }
  })

  const watchedRoute = watch('routeId')
  const watchedCapacity = watch('capacity')
  const watchedSelectedStudents = watch('selectedStudents') || []
  const [studentSearch, setStudentSearch] = useState('')

  // Filter students based on search
  const filteredStudents = students.filter(student => {
    if (!studentSearch.trim()) return true
    const searchLower = studentSearch.toLowerCase()
    return (
      student.firstName?.toLowerCase().includes(searchLower) ||
      student.lastName?.toLowerCase().includes(searchLower) ||
      student.admissionNumber?.toLowerCase().includes(searchLower) ||
      student.grade?.toLowerCase().includes(searchLower)
    )
  })

  // Load initial data
  useEffect(() => {
    loadFormData()
  }, [institutionId])

  // Update selected route when route changes
  useEffect(() => {
    if (watchedRoute) {
      const route = routes.find(r => r.id === watchedRoute)
      setSelectedRoute(route || null)
      
      // Auto-fill trip name based on route
      if (route && !initialData?.tripName) {
        setValue('tripName', `${watch('tripType')} - ${route.routeName}`)
      }
    }
  }, [watchedRoute, routes])

  const loadFormData = async () => {
    try {
      setLoading(true)

      // Single API call to get all form data
      const response = await getTripFormData(institutionId)
      const { transportSettings: settings, routes: routesData, vehicles: vehiclesData, drivers: driversData, students: studentsData } = response.data

      setTransportSettings(settings)
      setRoutes(routesData || [])
      setVehicles(vehiclesData || [])
      setDrivers(driversData || [])
      setStudents(studentsData || [])

      // Use transport settings defaults
      if (settings) {
        setValue('startTime', settings.defaultPickupTime || '07:30')
        setValue('endTime', settings.defaultDropoffTime || '16:00')
        setValue('autoCreateSessions', settings.autoCreateSessions ?? true)
      }

    } catch (error) {
      console.error('Failed to load form data:', error)
      toast.error('Failed to load form data')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: EnhancedTripForm) => {
    try {
      setSubmitting(true)

      // Validate transport is enabled
      if (!transportSettings?.enabled) {
        toast.error('Transport module is not enabled for this institution')
        return
      }

      // Validate capacity
      if (data.selectedStudents && data.selectedStudents.length > data.capacity) {
        toast.error(`Cannot select ${data.selectedStudents.length} students. Trip capacity is ${data.capacity}`)
        return
      }

      const response = await createEnhancedTrip({
        routeId: data.routeId,
        vehicleId: data.vehicleId,
        primaryDriverId: data.primaryDriverId,
        backupDriverId: data.backupDriverId,
        tripName: data.tripName,
        tripType: data.tripType,
        startTime: data.startTime,
        endTime: data.endTime,
        operatingDays: data.operatingDays,
        capacity: data.capacity,
        selectedStudents: data.selectedStudents,
        autoCreateSessions: data.autoCreateSessions,
        institutionId
      })

      toast.success(`Trip "${data.tripName}" created successfully!`)
      onSuccess?.(response.data.trip)

    } catch (error: any) {
      console.error('Failed to create trip:', error)
      toast.error(error.response?.data?.message || 'Failed to create trip')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!transportSettings?.enabled) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
          <div>
            <h3 className="font-medium text-yellow-900">Transport Module Disabled</h3>
            <p className="text-sm text-yellow-700">
              Transport module is not enabled for this institution. Please enable it in settings first.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200"
    >
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <TruckIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create Enhanced Trip</h2>
              <p className="text-sm text-gray-600">Configure trip with student selection and sessions</p>
            </div>
          </div>
          {onCancel && (
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
        {/* Basic Trip Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Trip Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip Name *
              </label>
              <Input
                {...register('tripName')}
                placeholder="e.g., Morning Pickup - Route A"
                error={errors.tripName?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip Type *
              </label>
              <select
                {...register('tripType')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="pickup">Morning Pickup</option>
                <option value="dropoff">Afternoon Dropoff</option>
                <option value="field_trip">Field Trip</option>
                <option value="emergency">Emergency Transport</option>
                <option value="custom">Custom Trip</option>
              </select>
              {errors.tripType && (
                <p className="mt-1 text-sm text-red-600">{errors.tripType.message}</p>
              )}
            </div>
          </div>

          {/* Operating Days Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Operating Days *
            </label>
            <Controller
              name="operatingDays"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => {
                        const currentDays = field.value || []
                        if (currentDays.includes(day.value)) {
                          field.onChange(currentDays.filter(d => d !== day.value))
                        } else {
                          field.onChange([...currentDays, day.value])
                        }
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        field.value?.includes(day.value)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
              )}
            />
            {errors.operatingDays && (
              <p className="mt-1 text-sm text-red-600">{errors.operatingDays.message}</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Sessions will be automatically created for selected days
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <Input
                type="time"
                {...register('startTime')}
                error={errors.startTime?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <Input
                type="time"
                {...register('endTime')}
                error={errors.endTime?.message}
              />
            </div>
          </div>
        </div>

        {/* Vehicle Assignment */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Vehicle & Driver Assignment
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Route *
              </label>
              <select
                {...register('routeId')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select Route</option>
                {routes.map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.routeNumber} - {route.routeName}
                  </option>
                ))}
              </select>
              {errors.routeId && (
                <p className="mt-1 text-sm text-red-600">{errors.routeId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle *
              </label>
              <select
                {...register('vehicleId')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map((vehicle: any) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.vehicleNumber} - {vehicle.make} {vehicle.vehicleModel}
                  </option>
                ))}
              </select>
              {errors.vehicleId && (
                <p className="mt-1 text-sm text-red-600">{errors.vehicleId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capacity *
              </label>
              <Input
                type="number"
                min="1"
                max="100"
                {...register('capacity', { valueAsNumber: true })}
                placeholder="25"
                error={errors.capacity?.message}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Driver *
              </label>
              <select
                {...register('primaryDriverId')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select Primary Driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName} - {driver.phone}
                  </option>
                ))}
              </select>
              {errors.primaryDriverId && (
                <p className="mt-1 text-sm text-red-600">{errors.primaryDriverId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Backup Driver (Optional)
              </label>
              <select
                {...register('backupDriverId')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select Backup Driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName} - {driver.phone}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Student Selection - Always Visible */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center space-x-2">
            <UsersIcon className="w-5 h-5 text-blue-600" />
            <span>Select Students *</span>
            <span className={`text-sm font-normal ${watchedSelectedStudents.length > 0 ? 'text-green-600' : 'text-red-500'}`}>
              ({watchedSelectedStudents.length}/{watchedCapacity} selected)
            </span>
          </h3>

          {errors.selectedStudents && (
            <p className="text-sm text-red-600">{errors.selectedStudents.message}</p>
          )}

          {selectedRoute && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <MapPinIcon className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">Route Stops</h4>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {selectedRoute.stops?.map((stop, index) => (
                  <div key={stop.stopId} className="text-xs bg-white rounded px-2 py-1 text-blue-800">
                    {index + 1}. {stop.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search students by name, admission number, or grade..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {studentSearch && (
              <button
                type="button"
                onClick={() => setStudentSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Count and Select All */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredStudents.length} of {students.length} students {studentSearch && '(filtered)'}
            </p>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  const allIds = filteredStudents.slice(0, watchedCapacity).map(s => s.id)
                  setValue('selectedStudents', allIds)
                }}
              >
                Select All (max {watchedCapacity})
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setValue('selectedStudents', [])}
              >
                Clear All
              </Button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
            <div className="p-4">
              <Controller
                name="selectedStudents"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredStudents.length === 0 ? (
                      <p className="col-span-2 text-center text-gray-500 py-4">
                        No students found matching "{studentSearch}"
                      </p>
                    ) : filteredStudents.map((student) => (
                      <label key={student.id} className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        field.value?.includes(student.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}>
                        <input
                          type="checkbox"
                          checked={field.value?.includes(student.id) || false}
                          onChange={(e) => {
                            const currentValue = field.value || []
                            if (e.target.checked) {
                              if (currentValue.length < watchedCapacity) {
                                field.onChange([...currentValue, student.id])
                              } else {
                                toast.error(`Trip capacity is ${watchedCapacity}. Cannot add more students.`)
                                e.target.checked = false
                              }
                            } else {
                              field.onChange(currentValue.filter(id => id !== student.id))
                            }
                          }}
                          disabled={
                            !field.value?.includes(student.id) &&
                            (field.value?.length || 0) >= watchedCapacity
                          }
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <UserIcon className="w-5 h-5 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {student.firstName} {student.lastName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {student.admissionNumber || ''} {student.grade ? `• ${student.grade}` : ''}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>

          {watchedSelectedStudents.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                {watchedSelectedStudents.length} student{watchedSelectedStudents.length !== 1 ? 's' : ''} selected for this trip
              </p>
            </div>
          )}
        </div>

        {/* Session Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Session Settings
          </h3>

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              {...register('autoCreateSessions')}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Auto-Create Daily Sessions</p>
              <p className="text-xs text-gray-500">
                Automatically create trip sessions for the next week
              </p>
            </div>
          </label>
        </div>

        {/* Submit Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          
          <Button
            type="submit"
            disabled={submitting}
            className="flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Creating Trip...</span>
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4" />
                <span>Create Trip</span>
              </>
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  )
}

export default EnhancedTripForm